import { forwardRef, useMemo } from "react";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import {
  DataGridPremium,
  type GridColDef,
  type GridPaginationModel,
  type GridRowsProp,
  type GridSortModel,
} from "@mui/x-data-grid-premium";
import { aiImpactAssessmentColumns } from "./assessmentCenterColumns";
import { withAssessmentCenterHeaderTooltips } from "./AssessmentCenterTruncatedColumnHeader";

const LOADING_BAR_HEIGHT_PX = 4;

/** Thin top loading bar aligned with the column header bottom edge. */
const AssessmentCenterGridLoadingOverlay = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function AssessmentCenterGridLoadingOverlay(props, ref) {
  const { className, ...other } = props;
  return (
    <Box
      ref={ref}
      className={className}
      {...other}
      sx={{
        position: "absolute",
        top: -4,
        left: 0,
        width: "100%",
        height: LOADING_BAR_HEIGHT_PX,
        lineHeight: 0,
      }}
    >
      <LinearProgress
        sx={{
          width: "100%",
          height: LOADING_BAR_HEIGHT_PX,
          display: "block",
          margin: 0,
        }}
      />
    </Box>
  );
});

const dataGridSx = {
  border: "none",
  "& .MuiDataGrid-columnSeparator": {
    display: "none",
  },
  "& .MuiDataGrid-topContainer": {
    position: "relative",
    borderBottom: "1px solid #F2F2F2",
    boxSizing: "border-box",
  },
  "& .MuiDataGrid-topContainer::after": {
    display: "none",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#F9FAFB",
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: "14px",
    fontWeight: 700,
    textTransform: "none",
    color: "#0D0D0D",
    minHeight: "48px !important",
    maxHeight: 48,
    borderBottom: "none",
  },
  "& .MuiDataGrid-columnHeadersInner": {
    backgroundColor: "#F9FAFB",
  },
  "& .MuiDataGrid-scrollbarFiller--header": {
    backgroundColor: "#F9FAFB",
  },
  "& .MuiDataGrid-columnHeader": {
    height: 48,
    maxHeight: 48,
    padding: "12px 24px",
    boxSizing: "border-box",
    backgroundColor: "#F9FAFB",
    borderRight: "none !important",
    borderBottom: "none !important",
  },
  "& .MuiDataGrid-filler--borderBottom, & .MuiDataGrid-scrollbarFiller--borderBottom":
    {
      borderBottom: "none !important",
    },
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    gap: "12px",
    minWidth: 0,
    overflow: "hidden",
  },
  "& .MuiDataGrid-columnHeaderTitleContainerContent": {
    minWidth: 0,
    overflow: "hidden",
    flex: 1,
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontFamily: '"Nunito Sans", sans-serif',
    fontWeight: 700,
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: 0,
    color: "#0D0D0D",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .MuiDataGrid-iconButtonContainer": {
    visibility: "visible",
    width: "auto",
  },
  "& .MuiDataGrid-sortIcon": {
    color: "#0D0D0D",
    opacity: 1,
  },
  "& .MuiDataGrid-cell": {
    fontSize: "14px",
    color: "#262626",
    fontWeight: 500,
    borderRight: "none !important",
    borderTop: "none !important",
  },
  "& .impacted-concepts-cell": {
    display: "flex !important",
    alignItems: "center !important",
    justifyContent: "flex-start !important",
    "& .MuiDataGrid-cellContent": {
      height: "100%",
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
    },
  },
  "& .MuiDataGrid-columnHeader--pinnedRight": {
    borderLeft: "none !important",
    boxShadow: "none",
  },
  "& .MuiDataGrid-cell--pinnedRight": {
    borderLeft: "none !important",
    boxShadow: "none",
  },
  "& .assessment-center-even-row, & .assessment-center-even-row .MuiDataGrid-cell":
    {
      backgroundColor: "#F2F2F2",
  },
  "& .MuiDataGrid-main": {
    overflow: "hidden",
  },
  "& .MuiDataGrid-virtualScroller": {
    overflow: "auto",
  },
  /* While loading, replace the topContainer bottom rule with the progress bar. */
  "& .MuiDataGrid-virtualScroller:has(.MuiDataGrid-overlayWrapper) .MuiDataGrid-topContainer":
    {
      borderBottom: "none",
    },
  /*
   * MUI sets overlayWrapper to 0×0; anchor the bar on the header bottom edge
   * (headersTotalHeight includes the topContainer border).
   */
  "& .MuiDataGrid-overlayWrapper": {
    top: `calc(var(--DataGrid-headersTotalHeight) - ${LOADING_BAR_HEIGHT_PX}px)`,
    width:
      "calc(100% - (var(--DataGrid-hasScrollY) * var(--DataGrid-scrollbarSize))) !important",
    height: `${LOADING_BAR_HEIGHT_PX}px !important`,
    zIndex: 50,
    overflow: "visible",
    pointerEvents: "none",
  },
  "& .MuiDataGrid-overlayWrapperInner": {
    height: `${LOADING_BAR_HEIGHT_PX}px !important`,
    width: "100% !important",
    overflow: "visible",
    position: "relative",
  },
} as const;

export type AssessmentCenterImpactDataGridProps = Readonly<{
  /** Section title shown above the grid (e.g. AI vs Manual). Omit when embedding under a parent card (e.g. with filter bar). */
  title?: string | null;
  /** Passed to DataGrid aria-label */
  ariaLabel: string;
  /**
   * When true, only renders the grid (no outer card/title); parent supplies chrome.
   */
  embedded?: boolean;
  /** Table rows (client or server-backed). */
  rows?: GridRowsProp;
  loading?: boolean;
  /** Server pagination: total row count across pages. */
  rowCount?: number;
  paginationModel?: GridPaginationModel;
  onPaginationModelChange?: (model: GridPaginationModel) => void;
  paginationMode?: "client" | "server";
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  sortingMode?: "client" | "server";
  columns?: GridColDef[];
  disableColumnSorting?: boolean;
  /** Fixed pixel height (e.g. modals). Omit with `fillHeight` to grow in a flex parent. */
  height?: number;
  /** Fill remaining vertical space in a flex column layout (Assessment Center tabs). */
  fillHeight?: boolean;
  /** Rows-per-page choices (server pagination). */
  pageSizeOptions?: readonly number[];
  /** Allow action-column tooltips to escape clipped grid cells (e.g. AI Results modal). */
  actionCellOverflowVisible?: boolean;
}>;

/**
 * Assessment Center tables using the same DataGrid Premium stack as Regulations Next home list.
 */
export default function AssessmentCenterImpactDataGrid({
  title,
  ariaLabel,
  embedded = false,
  rows = [],
  loading = false,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  paginationMode = "client",
  sortModel,
  onSortModelChange,
  sortingMode = "client",
  columns: columnsProp,
  disableColumnSorting = false,
  height,
  fillHeight: fillHeightProp = false,
  pageSizeOptions = [10, 20, 50, 100],
  actionCellOverflowVisible = false,
}: AssessmentCenterImpactDataGridProps) {
  const columns = useMemo(
    () =>
      withAssessmentCenterHeaderTooltips(
        columnsProp ?? aiImpactAssessmentColumns,
      ),
    [columnsProp],
  );

  const serverPagination = paginationMode === "server";
  const fillHeight =
    fillHeightProp || (embedded && height === undefined);

  const grid = (
    <Box
      sx={
        fillHeight
          ? {
              flex: 1,
              minHeight: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }
          : { height: height ?? 480, width: "100%" }
      }
    >
      <DataGridPremium
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id as string}
        disableRowSelectionOnClick
        loading={loading}
        slots={{ loadingOverlay: AssessmentCenterGridLoadingOverlay }}
        hideFooter={!serverPagination}
        pagination={serverPagination}
        paginationMode={paginationMode}
        rowCount={serverPagination ? (rowCount ?? 0) : undefined}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[...pageSizeOptions]}
        disableColumnFilter
        disableColumnMenu
        showColumnVerticalBorder={false}
        disableColumnSorting={disableColumnSorting}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 1
            ? "assessment-center-even-row"
            : ""
        }
        sortingMode={sortingMode}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        aria-label={ariaLabel}
        density="standard"
        initialState={{
          pinnedColumns: { right: ["actions"] },
        }}
        localeText={{
          noRowsLabel: "No data found",
        }}
        sx={{
          ...dataGridSx,
          ...(fillHeight ? { height: "100%" } : {}),
          ...(actionCellOverflowVisible
            ? {
                "& .MuiDataGrid-cell[data-field='actions'], & .MuiDataGrid-cell[data-field='status']":
                  {
                    overflow: "visible",
                  },
              }
            : {}),
        }}
      />
    </Box>
  );

  if (embedded) {
    return grid;
  }

  return (
    <Box
      sx={{
        border: "1px solid #e5e7eb",
        borderRadius: 2,
        backgroundColor: "#fff",
        p: 2,
      }}
    >
      {title != null && title !== "" && (
        <Typography sx={{ color: "#111827", fontWeight: 600, mb: 2 }}>
          {title}
        </Typography>
      )}
      {grid}
    </Box>
  );
}
